import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { DOMAIN_VALIDATION_REGEX, FormTextField } from 'src/components/Forms';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer } from 'src/components/ModalContainer';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { PartnerDomainFormFields, PartnerDomainFormModel } from './types';

const INVALID_DOMAIN_MESSAGE = 'Should be a valid domain';
const DUPLICATE_DOMAIN_MESSAGE = 'Domain already added';

const defaultValues: PartnerDomainFormModel = { [PartnerDomainFormFields.Domain]: '' };

export const AddPartnerDomainModal = () => {
  const { closeModal } = useModalMethods(Modals.AddPartnerDomainModal);
  const { isOpened, modalProps } = useModalState(Modals.AddPartnerDomainModal);

  const formMethods = useForm<PartnerDomainFormModel>({ defaultValues, mode: 'onChange' });
  const { formState, getValues, reset } = formMethods;

  const closeModalAndResetForm = useCallback(() => {
    reset();
    closeModal();
  }, [reset, closeModal]);

  const handleAddDomain = () => {
    modalProps?.onSubmit(getValues(PartnerDomainFormFields.Domain));
    closeModalAndResetForm();
  };

  const validateDomain = (domain: string) => {
    if ((modalProps?.existingDomains ?? []).includes(domain)) {
      return DUPLICATE_DOMAIN_MESSAGE;
    }

    return DOMAIN_VALIDATION_REGEX.test(domain) || INVALID_DOMAIN_MESSAGE;
  };

  return (
    <ModalContainer
      open={isOpened}
      size="small"
      title="Add a new allowed domain for sign up"
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'add-partner-domain-cancel' },
        {
          label: 'Add domain',
          kind: 'primary',
          onClick: handleAddDomain,
          disabled: !formState.isValid,
          testId: 'add-partner-domain-submit',
        },
      ]}
      onClose={closeModalAndResetForm}
      data-testid="add-partner-domain-modal"
    >
      <GenericForm<PartnerDomainFormModel> formMethods={formMethods}>
        <FormTextField
          type="textfield"
          name={PartnerDomainFormFields.Domain}
          label="Domain"
          required
          rules={{ validate: validateDomain }}
          showErrorText
          textFieldProps={{ inputProps: { 'data-testid': 'add-partner-domain-input' } }}
        />
      </GenericForm>
    </ModalContainer>
  );
};
